import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import {
  PIPELINE_COLUMN_PREFERENCE_CHANGE_EVENT,
  persistPipelineColumnPreference,
  readPipelineColumnPreference,
  type PipelineColumnPreferenceScope,
} from '../utils/pipeline/column-preference.util';

/**
 * Result returned by the Communications pipeline column preference hook.
 */
export interface UsePipelineColumnPreferencesResult {
  /** Explicit or default-effective collapsed status ids for the active board. */
  collapsedStatusIds: number[];
  /** Whether the tenant scope is still loading for the current session. */
  loading: boolean;
  /** Whether every AI-linked status is currently collapsed. */
  areAllAiStatusesCollapsed: boolean;
  /** Whether every status in the current funnel is currently collapsed. */
  areAllStatusesCollapsed: boolean;
  /** Returns true when the provided status id is collapsed in the active board. */
  isStatusCollapsed: (statusId: number) => boolean;
  /** Toggles a single status between expanded and collapsed states. */
  toggleStatus: (statusId: number) => void;
  /** Collapses every AI-linked status while preserving other collapsed columns. */
  collapseAiStatuses: () => void;
  /** Expands every AI-linked status while preserving other collapsed columns. */
  expandAiStatuses: () => void;
  /** Collapses every status in the active funnel. */
  collapseAllStatuses: () => void;
  /** Expands every status in the active funnel. */
  expandAllStatuses: () => void;
}

interface UsePipelineColumnPreferencesParams {
  funnelId?: number;
  statusIds: number[];
  aiStatusIds: number[];
}

interface PipelineColumnPreferenceSnapshot {
  scopeKey: string | null;
  collapsedStatusIds: number[];
  hasStoredPreference: boolean;
}

const getScopeKey = (
  scope: PipelineColumnPreferenceScope | null
): string | null => (scope ? `${scope.tenantId}:${scope.funnelId}` : null);

const sortStatusIds = (statusIds: number[]): number[] =>
  Array.from(new Set(statusIds)).sort((left, right) => left - right);

const areSameStatusIds = (left: number[], right: number[]): boolean =>
  left.length === right.length &&
  left.every((statusId, index) => statusId === right[index]);

const sanitizeCollapsedStatusIds = (
  collapsedStatusIds: number[],
  validStatusIds: number[]
): number[] => {
  const validStatusIdSet = new Set(validStatusIds);

  return sortStatusIds(
    collapsedStatusIds.filter((statusId) => validStatusIdSet.has(statusId))
  );
};

const resolveSnapshot = (
  scope: PipelineColumnPreferenceScope | null,
  validStatusIds: number[],
  aiStatusIds: number[]
): PipelineColumnPreferenceSnapshot => {
  const scopeKey = getScopeKey(scope);

  if (!scope) {
    return {
      scopeKey,
      collapsedStatusIds: [],
      hasStoredPreference: false,
    };
  }

  const { preference, hasStoredPreference } =
    readPipelineColumnPreference(scope);

  if (hasStoredPreference && preference) {
    return {
      scopeKey,
      collapsedStatusIds: sanitizeCollapsedStatusIds(
        preference.collapsedStatusIds,
        validStatusIds
      ),
      hasStoredPreference: true,
    };
  }

  return {
    scopeKey,
    collapsedStatusIds: sanitizeCollapsedStatusIds(aiStatusIds, validStatusIds),
    hasStoredPreference: false,
  };
};

export const usePipelineColumnPreferences = ({
  funnelId,
  statusIds,
  aiStatusIds,
}: UsePipelineColumnPreferencesParams): UsePipelineColumnPreferencesResult => {
  const { assumedTenantId } = useSupportTenant();
  const { user: currentUser, loading: loadingCurrentUser } = useCurrentUser();

  const resolvedTenantId =
    assumedTenantId ??
    currentUser?.tenantId ??
    (loadingCurrentUser ? null : 'unknown');
  const loading = !assumedTenantId && loadingCurrentUser;

  const sortedStatusIds = useMemo(() => sortStatusIds(statusIds), [statusIds]);
  const sortedAiStatusIds = useMemo(
    () => sortStatusIds(aiStatusIds),
    [aiStatusIds]
  );

  const scope = useMemo<PipelineColumnPreferenceScope | null>(() => {
    if (!resolvedTenantId || !funnelId) {
      return null;
    }

    return {
      tenantId: resolvedTenantId,
      funnelId,
    };
  }, [resolvedTenantId, funnelId]);

  const [snapshot, setSnapshot] = useState<PipelineColumnPreferenceSnapshot>(
    () => resolveSnapshot(scope, sortedStatusIds, sortedAiStatusIds)
  );

  useEffect(() => {
    const nextSnapshot = resolveSnapshot(
      scope,
      sortedStatusIds,
      sortedAiStatusIds
    );

    setSnapshot((currentSnapshot) =>
      currentSnapshot.scopeKey === nextSnapshot.scopeKey &&
      currentSnapshot.hasStoredPreference ===
        nextSnapshot.hasStoredPreference &&
      areSameStatusIds(
        currentSnapshot.collapsedStatusIds,
        nextSnapshot.collapsedStatusIds
      )
        ? currentSnapshot
        : nextSnapshot
    );
  }, [scope, sortedAiStatusIds, sortedStatusIds]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncSnapshot = () => {
      const nextSnapshot = resolveSnapshot(
        scope,
        sortedStatusIds,
        sortedAiStatusIds
      );

      setSnapshot((currentSnapshot) =>
        currentSnapshot.scopeKey === nextSnapshot.scopeKey &&
        currentSnapshot.hasStoredPreference ===
          nextSnapshot.hasStoredPreference &&
        areSameStatusIds(
          currentSnapshot.collapsedStatusIds,
          nextSnapshot.collapsedStatusIds
        )
          ? currentSnapshot
          : nextSnapshot
      );
    };

    window.addEventListener('storage', syncSnapshot);
    window.addEventListener(
      PIPELINE_COLUMN_PREFERENCE_CHANGE_EVENT,
      syncSnapshot
    );

    return () => {
      window.removeEventListener('storage', syncSnapshot);
      window.removeEventListener(
        PIPELINE_COLUMN_PREFERENCE_CHANGE_EVENT,
        syncSnapshot
      );
    };
  }, [scope, sortedAiStatusIds, sortedStatusIds]);

  useEffect(() => {
    if (!scope || !snapshot.hasStoredPreference) {
      return;
    }

    if (snapshot.scopeKey !== getScopeKey(scope)) {
      return;
    }

    const { preference } = readPipelineColumnPreference(scope);
    const persistedIds = sortStatusIds(preference?.collapsedStatusIds ?? []);

    if (areSameStatusIds(persistedIds, snapshot.collapsedStatusIds)) {
      return;
    }

    persistPipelineColumnPreference(scope, snapshot.collapsedStatusIds);
  }, [
    scope,
    snapshot.collapsedStatusIds,
    snapshot.hasStoredPreference,
    snapshot.scopeKey,
  ]);

  const persistCollapsedStatusIds = useCallback(
    (nextCollapsedStatusIds: number[]) => {
      if (!scope) {
        return;
      }

      const sanitizedIds = sanitizeCollapsedStatusIds(
        nextCollapsedStatusIds,
        sortedStatusIds
      );

      persistPipelineColumnPreference(scope, sanitizedIds);
      setSnapshot({
        scopeKey: getScopeKey(scope),
        collapsedStatusIds: sanitizedIds,
        hasStoredPreference: true,
      });
    },
    [scope, sortedStatusIds]
  );

  const toggleStatus = useCallback(
    (statusId: number) => {
      if (snapshot.collapsedStatusIds.includes(statusId)) {
        persistCollapsedStatusIds(
          snapshot.collapsedStatusIds.filter((id) => id !== statusId)
        );
        return;
      }

      persistCollapsedStatusIds([...snapshot.collapsedStatusIds, statusId]);
    },
    [persistCollapsedStatusIds, snapshot.collapsedStatusIds]
  );

  const collapseAiStatuses = useCallback(() => {
    persistCollapsedStatusIds([
      ...snapshot.collapsedStatusIds,
      ...sortedAiStatusIds,
    ]);
  }, [
    persistCollapsedStatusIds,
    snapshot.collapsedStatusIds,
    sortedAiStatusIds,
  ]);

  const expandAiStatuses = useCallback(() => {
    const aiStatusIdSet = new Set(sortedAiStatusIds);

    persistCollapsedStatusIds(
      snapshot.collapsedStatusIds.filter(
        (statusId) => !aiStatusIdSet.has(statusId)
      )
    );
  }, [
    persistCollapsedStatusIds,
    snapshot.collapsedStatusIds,
    sortedAiStatusIds,
  ]);

  const collapseAllStatuses = useCallback(() => {
    persistCollapsedStatusIds(sortedStatusIds);
  }, [persistCollapsedStatusIds, sortedStatusIds]);

  const expandAllStatuses = useCallback(() => {
    persistCollapsedStatusIds([]);
  }, [persistCollapsedStatusIds]);

  const collapsedStatusIdSet = useMemo(
    () => new Set(snapshot.collapsedStatusIds),
    [snapshot.collapsedStatusIds]
  );

  const areAllAiStatusesCollapsed =
    sortedAiStatusIds.length > 0 &&
    sortedAiStatusIds.every((statusId) => collapsedStatusIdSet.has(statusId));
  const areAllStatusesCollapsed =
    sortedStatusIds.length > 0 &&
    sortedStatusIds.every((statusId) => collapsedStatusIdSet.has(statusId));

  return {
    collapsedStatusIds: snapshot.collapsedStatusIds,
    loading,
    areAllAiStatusesCollapsed,
    areAllStatusesCollapsed,
    isStatusCollapsed: (statusId: number) => collapsedStatusIdSet.has(statusId),
    toggleStatus,
    collapseAiStatuses,
    expandAiStatuses,
    collapseAllStatuses,
    expandAllStatuses,
  };
};
